import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, ChevronDown } from 'lucide-react';
import { forwardRef, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { shortcutProps, useShortcut } from '../keys/runtime.js';
import { canSwitchEffort, effortsFor, modelParts, pickerModels, } from '../lib/models.js';
import { Button } from './primitives.js';
/**
 * The composer's model chip: a readable label (`Opus 5.5 · 1M`), the full id in the tooltip and
 * aria-label. The context suffix hides on narrow widths; the name is never cut mid-word.
 */
export const ModelChip = forwardRef(function ModelChip({ model, effort, onClick, expanded, disabled, hint }, ref) {
    const parts = modelParts(model);
    return (_jsxs("button", { ref: ref, type: "button", className: "vk-modelchip", onClick: onClick, ...(onClick
            ? shortcutProps('composer.model', hint ? `${model} — ${hint}` : model)
            : { title: hint ? `${model} — ${hint}` : model }), "aria-label": `Model ${model}${effort ? `, effort ${effort}` : ''}. Change`, "aria-haspopup": onClick ? 'dialog' : undefined, "aria-expanded": onClick ? !!expanded : undefined, "aria-disabled": disabled || undefined, children: [_jsx("span", { className: "vk-modelchip__name", children: parts?.name ?? model }), parts?.context ? _jsxs("span", { className: "vk-modelchip__ctx", children: ["\u00B7 ", parts.context] }) : null, effort ? _jsx("span", { className: "vk-meta", children: effort }) : null, _jsx(ChevronDown, { size: 12, "aria-hidden": "true" })] }));
});
/** Popover anchored above the model chip: choose a model (and Codex effort), apply. */
export function ModelPicker({ agent, model, effort, disabledReason, onApply, onError, cliModels, }) {
    const [open, setOpen] = useState(false);
    const [optimistic, setOptimistic] = useState();
    const [choice, setChoice] = useState(model);
    const [choiceEffort, setChoiceEffort] = useState(effort);
    const [custom, setCustom] = useState('');
    const [pos, setPos] = useState();
    const chip = useRef(null);
    const pop = useRef(null);
    const titleId = useId();
    const shownModel = optimistic?.model ?? model;
    const shownEffort = optimistic ? optimistic.effort : effort;
    const listed = pickerModels(agent, shownModel, cliModels);
    const [showOlder, setShowOlder] = useState(false);
    const currentId = listed.current ?? shownModel;
    const efforts = effortsFor(agent);
    const effortOk = canSwitchEffort(agent);
    const freeText = agent !== 'claude';
    // A real change reported by the run supersedes the optimistic value.
    // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the run's values change
    useEffect(() => setOptimistic(undefined), [model, effort]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: place only reads refs and window
    useLayoutEffect(() => {
        if (!open)
            return;
        const place = () => {
            const r = chip.current?.getBoundingClientRect();
            if (!r)
                return;
            const width = Math.min(340, window.innerWidth - 24);
            const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12));
            setPos({ left, bottom: window.innerHeight - r.top + 8, width });
        };
        place();
        window.addEventListener('resize', place);
        return () => window.removeEventListener('resize', place);
    }, [open]);
    useEffect(() => {
        if (!open)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                chip.current?.focus();
            }
        };
        const onDown = (e) => {
            const t = e.target;
            if (!pop.current?.contains(t) && !chip.current?.contains(t))
                setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('pointerdown', onDown);
        pop.current?.querySelector('[aria-checked="true"], input, button')?.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('pointerdown', onDown);
        };
    }, [open]);
    const toggle = () => {
        if (!open) {
            setChoice(currentId);
            setChoiceEffort(shownEffort);
            setShowOlder(false);
            setCustom('');
        }
        setOpen(!open);
    };
    useShortcut('composer.model', toggle);
    const target = custom.trim() || choice;
    const changed = target !== currentId || (effortOk && choiceEffort !== shownEffort);
    const apply = async () => {
        if (disabledReason || !changed)
            return;
        const next = {
            model: target,
            ...(effortOk && choiceEffort ? { effort: choiceEffort } : {}),
        };
        const prev = optimistic;
        setOptimistic(next);
        setOpen(false);
        chip.current?.focus();
        try {
            await onApply(next.model, next.effort);
        }
        catch (e) {
            setOptimistic(prev);
            onError?.(e);
        }
    };
    let popover = null;
    if (open && pos) {
        popover = createPortal(_jsxs("div", { ref: pop, className: "vk-modelpop", role: "dialog", "aria-labelledby": titleId, style: { left: pos.left, bottom: pos.bottom, width: pos.width }, children: [_jsx("div", { id: titleId, className: "vk-modelpop__title", children: "Model" }), disabledReason ? (_jsx("p", { className: "vk-modelpop__note", role: "status", children: disabledReason })) : null, _jsx("div", { role: "radiogroup", "aria-labelledby": titleId, className: "vk-modelpop__list", children: [...listed.main, ...(showOlder ? listed.older : [])].map((o) => (_jsxs("button", { type: "button", role: "radio", "aria-checked": choice === o.id && !custom.trim(), disabled: !!disabledReason, className: "vk-modelpop__opt", title: o.id, onClick: () => {
                            setChoice(o.id);
                            setCustom('');
                        }, children: [_jsxs("span", { className: "vk-modelpop__lbl", children: [_jsxs("span", { children: [o.label, o.id === currentId ? (_jsx("span", { className: "vk-modelpop__hint", children: ' · now' })) : null] }), o.hint ? _jsx("span", { className: "vk-modelpop__hint", children: o.hint }) : null] }), choice === o.id && !custom.trim() ? _jsx(Check, { size: 14, "aria-hidden": "true" }) : null] }, o.id))) }), listed.older.length ? (_jsx("button", { type: "button", className: "vk-modelpop__older", "aria-expanded": showOlder, onClick: () => setShowOlder(!showOlder), children: showOlder ? 'Hide older models' : `Show older (${listed.older.length})` })) : null, freeText ? (_jsxs("label", { className: "vk-modelpop__field", children: [_jsx("span", { children: "Model id" }), _jsx("input", { className: "vk-modelpop__input", value: custom, placeholder: shownModel, disabled: !!disabledReason, spellCheck: false, autoCapitalize: "none", onChange: (e) => setCustom(e.target.value) })] })) : null, effortOk ? (_jsxs(_Fragment, { children: [_jsx("div", { id: `${titleId}-effort`, className: "vk-modelpop__title", children: "Effort" }), _jsx("div", { role: "radiogroup", "aria-labelledby": `${titleId}-effort`, className: "vk-modelpop__seg", children: efforts.map((f) => (_jsx("button", { type: "button", role: "radio", "aria-checked": choiceEffort === f, disabled: !!disabledReason, className: "vk-modelpop__segbtn", onClick: () => setChoiceEffort(f), children: f }, f))) })] })) : (_jsx("p", { className: "vk-modelpop__note", children: "Effort is fixed when a Claude session starts." })), _jsxs("div", { className: "vk-modelpop__foot", children: [_jsx(Button, { onClick: () => setOpen(false), children: "Cancel" }), _jsx(Button, { variant: "primary", disabled: !!disabledReason || !changed, onClick: () => void apply(), children: "Apply" })] })] }), document.body);
    }
    return (_jsxs(_Fragment, { children: [_jsx(ModelChip, { ref: chip, model: shownModel, ...(shownEffort ? { effort: shownEffort } : {}), expanded: open, onClick: toggle, ...(disabledReason ? { disabled: true, hint: disabledReason } : {}) }), popover] }));
}
//# sourceMappingURL=ModelPicker.js.map